import React, { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Skeleton } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faChevronDown, faHotel, faShip, faTicket, IconDefinition } from '@fortawesome/free-solid-svg-icons';


import { CertificateStatusEnum, isInstalmentWalletPaymentType, ItemTypeEnum, IUserCertificate } from '@share/common-types';
import { Routes } from '@share/constants';

import { itemTypeMessageId } from '../../../certificates/components/certificate-card/components/certificate-item-tag/utils';
import { InstallmentProgress } from '../../../certificates/wallet-drawer/components/installment-progress';
import { WalletItem } from '../../../certificates/wallet-drawer/components/wallet-item';
import { useWalletCardInstallments } from '../../../certificates/wallet-drawer/components/wallet-card/hooks';
import {
  getWalletStatusLabel,
  resolveInstallmentProgressProps,
  shouldShowExpiryOnFullPaymentNote,
  shouldShowWalletUpdatePaymentCard,
} from '../../../certificates/wallet-drawer/utils';

import { getCertificateImage } from './images';
import styles from './styles.module.scss';

// Same lazily loaded Stripe block the wallet drawer uses to replace a failed instalment card.
const UpdatePaymentBlock = lazy(() => import('../../../certificates/wallet-drawer/components/wallet-card/wallet-card-update-payment-block'));

const ITEM_ICONS: Record<ItemTypeEnum, IconDefinition> = {
  [ItemTypeEnum.Hotel]: faHotel,
  [ItemTypeEnum.Condo]: faBuilding,
  [ItemTypeEnum.Cruise]: faShip,
};

const DAY_MS = 86400000;

const STATUS_TONE: Partial<Record<CertificateStatusEnum, string>> = {
  [CertificateStatusEnum.Active]: 'ok',
  [CertificateStatusEnum.PartiallyRedeemed]: 'ok',
  [CertificateStatusEnum.Pending]: 'info',
  [CertificateStatusEnum.New]: 'info',
  [CertificateStatusEnum.Canceled]: 'danger',
};

interface IProps {
  certificate: IUserCertificate;
  accountUsername: string;
  fetchCertificates: () => void;
}

/**
 * A certificate in the hub layout. Behaviour is the wallet drawer's `WalletCard` (same hooks, the same
 * redeem items, instalment progress and update-card block); only the layout is new.
 */
export const AccountCertificateCard = ({ certificate, accountUsername, fetchCertificates }: IProps): React.ReactElement => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const installmentRefetchErrorMessage = useMemo(
    () => intl.formatMessage({ id: 'certificates.wallet.refetchInstallmentsFailed' }),
    [intl],
  );

  const {
    fetchedInstallmentSchedule,
    fetchedCertificateTotalPrice,
    isInstallmentsLoading,
    shouldFetchInstallments,
    refetchInstallments,
  } = useWalletCardInstallments({
    certificateId: certificate.id,
    paymentType: certificate.paymentType,
    status: certificate.status,
    isExpanded,
    installmentRefetchErrorMessage,
  });

  const formatDate = (value: string | null | undefined): string => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? intl.formatDate(date, { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  };

  const purchased = formatDate(certificate.purchasedAt);
  const expires = formatDate(certificate.expiresAt);
  const expiryOnFullPayment = shouldShowExpiryOnFullPaymentNote({
    expiresText: expires,
    paymentType: certificate.paymentType,
    status: certificate.status,
  });

  const instalmentProgressProps = useMemo(() => {
    if (!isInstalmentWalletPaymentType(certificate.paymentType)) {
      return null;
    }
    return resolveInstallmentProgressProps({
      certificate,
      instalmentSchedule: fetchedInstallmentSchedule,
      intl,
      totalPriceFromInstallmentsResponse: fetchedCertificateTotalPrice,
    });
  }, [certificate, fetchedCertificateTotalPrice, fetchedInstallmentSchedule, intl]);

  const showUpdatePaymentCard = shouldShowWalletUpdatePaymentCard(certificate);

  const handleAfterCardUpdated = useCallback((): void => {
    const instalments = fetchedInstallmentSchedule?.instalments || [];
    if (instalments[instalments.length - 1]?.status === 'failed') {
      fetchCertificates();
    } else if (instalments.some(instalment => instalment.status === 'failed')) {
      refetchInstallments().catch((error: unknown) => console.error('Account hub: instalments refetch failed', error));
    }
  }, [fetchCertificates, fetchedInstallmentSchedule, refetchInstallments]);

  const showInstallmentSkeleton = isExpanded && shouldFetchInstallments && isInstallmentsLoading;
  const showInstallmentProgress = isExpanded && shouldFetchInstallments && !isInstallmentsLoading && instalmentProgressProps !== null;
  const showInstallmentsPanel = isExpanded && !showInstallmentSkeleton && (showInstallmentProgress || showUpdatePaymentCard);

  const itemTypes = Array.from(new Set((certificate.items || []).map(item => item.type)));
  const totalNights = (certificate.items || []).reduce((total, item) => total + (item.nights || 0), 0);
  const tone = STATUS_TONE[certificate.status] || 'neutral';
  const panelId = `certificate-${certificate.id}-nights`;

  const image = getCertificateImage(certificate.imageUrl, itemTypes[0], imageFailed);
  const typeLabel = itemTypes.length
    ? intl.formatMessage(
      { id: 'my.account.hub.certificates.type_label' },
      { types: itemTypes.map(type => intl.formatMessage({ id: itemTypeMessageId(type) })).join(' + ') },
    )
    : intl.formatMessage({ id: 'my.account.hub.section.certificates' });

  const isPending = certificate.status === CertificateStatusEnum.Pending || certificate.status === CertificateStatusEnum.New;
  const isUsable = certificate.status === CertificateStatusEnum.Active || certificate.status === CertificateStatusEnum.PartiallyRedeemed;
  const canExpand = isUsable || isPending;

  // Right-hand stub: the one date or number that matters for this status.
  const renderStub = () => {
    const bigDate = (value: string | undefined) => {
      const date = value ? new Date(value) : null;
      if (!date || Number.isNaN(date.getTime())) {
        return null;
      }
      return {
        day: intl.formatDate(date, { day: '2-digit', month: 'short' }),
        year: date.getFullYear(),
        daysLeft: Math.ceil((date.getTime() - Date.now()) / DAY_MS),
      };
    };

    if (isUsable) {
      const expiry = bigDate(certificate.expiresAt);
      if (expiry) {
        return (
          <>
            <span className={styles.stubLabel}>{intl.formatMessage({ id: 'my.account.hub.certificates.expires' })}</span>
            <span className={styles.stubValue}>{expiry.day}</span>
            <span className={styles.stubNote}>
              {expiry.daysLeft > 0
                ? intl.formatMessage({ id: 'my.account.hub.certificates.days_left' }, { year: expiry.year, days: expiry.daysLeft })
                : expiry.year}
            </span>
          </>
        );
      }
    }
    if (isPending || !totalNights) {
      return (
        <>
          <span className={styles.stubLabel}>{intl.formatMessage({ id: 'my.account.hub.certificates.nights_label' })}</span>
          <span className={styles.stubValue}>{totalNights || '—'}</span>
          {isPending ? <span className={styles.stubNote}>{intl.formatMessage({ id: 'my.account.hub.certificates.usable_when_paid' })}</span> : null}
        </>
      );
    }
    const ended = bigDate(certificate.updatedAt || certificate.expiresAt);
    return (
      <>
        <span className={styles.stubLabel}>{getWalletStatusLabel(certificate.status, intl)}</span>
        <span className={styles.stubValue}>{ended ? ended.day : totalNights}</span>
        {ended ? <span className={styles.stubNote}>{ended.year}</span> : null}
      </>
    );
  };

  return (
    <article className={styles.card}>
      <div className={styles.cardMain}>
        <div className={styles.media}>
          <img src={image} alt="" loading="lazy" className={styles.mediaImage} onError={() => setImageFailed(true)} />
        </div>

        <div className={styles.cardBody}>
          <div className={styles.cardTitleRow}>
            <div className={styles.cardTitleBlock}>
              <span className={styles.typeLabel}>{typeLabel}</span>
              <h3 className={styles.cardName}>{certificate.name}</h3>
              <p className={styles.cardCode}>{certificate.code}</p>
            </div>
            <span className={`${styles.status} ${styles[`status_${tone}`]}`}>{getWalletStatusLabel(certificate.status, intl)}</span>
          </div>

          {certificate.items?.length ? (
            <ul className={styles.chips} aria-label={intl.formatMessage({ id: 'my.account.hub.certificates.includes' })}>
              {certificate.items.map(item => (
                <li key={item.id} className={styles.chip}>
                  <FontAwesomeIcon icon={ITEM_ICONS[item.type] || faTicket} aria-hidden="true" />
                  {intl.formatMessage({ id: 'certificates.card.tag.nights' }, { type: item.type, nights: item.nights })}
                </li>
              ))}
            </ul>
          ) : null}

          {purchased || expiryOnFullPayment ? (
            <p className={styles.meta}>
              {[
                purchased ? intl.formatMessage({ id: 'my.account.hub.certificates.purchased_on' }, { date: purchased }) : '',
                expiryOnFullPayment ? intl.formatMessage({ id: 'certificates.wallet.expiryOnFullPayment' }) : '',
              ].filter(Boolean).join(' · ')}
            </p>
          ) : null}

          <div className={styles.actions}>
            {canExpand ? (
              <button
                type="button"
                className={styles.toggleButton}
                aria-expanded={isExpanded}
                aria-controls={panelId}
                onClick={() => setIsExpanded(value => !value)}
              >
                {intl.formatMessage({
                  id: isExpanded
                    ? 'my.account.hub.certificates.hide_nights'
                    : isPending ? 'my.account.hub.certificates.payment_and_nights' : 'my.account.hub.certificates.use_nights',
                })}
                <FontAwesomeIcon icon={faChevronDown} className={isExpanded ? styles.chevronOpen : styles.chevron} aria-hidden="true" />
              </button>
            ) : null}
            <Link to={`/${accountUsername}${Routes.Certificates}/${certificate.id}`} className={styles.linkButton}>
              {intl.formatMessage({ id: 'certificates.wallet.seeDetails' })}
            </Link>
          </div>
        </div>

        <div className={styles.stub}>{renderStub()}</div>
      </div>

      {isExpanded ? (
        <div id={panelId} className={styles.panel}>
          <div className={styles.items}>
            {certificate.items.map(item => (
              <WalletItem
                key={item.id}
                item={item}
                accountUsername={accountUsername}
                certificateCode={certificate.code}
                certificateTotalPrice={certificate.totalPrice}
                certificateStatus={certificate.status}
                certificateExpiresAt={certificate.expiresAt}
                certificateUpdatedAt={certificate.updatedAt}
              />
            ))}
          </div>
          {showInstallmentSkeleton ? <Skeleton active paragraph={{ rows: 3 }} title={{ width: '40%' }} /> : null}
          {showInstallmentsPanel ? (
            <InstallmentProgress
              progress={showInstallmentProgress ? instalmentProgressProps : null}
              footer={showUpdatePaymentCard ? (
                <Suspense fallback={<Skeleton active paragraph={{ rows: 2 }} title={{ width: '40%' }} />}>
                  <UpdatePaymentBlock
                    certificateId={certificate.id}
                    certificateName={certificate.name}
                    isWalletExpanded={isExpanded}
                    onCardUpdated={handleAfterCardUpdated}
                  />
                </Suspense>
              ) : undefined}
            />
          ) : null}
        </div>
      ) : null}
    </article>
  );
};
